// src/components/EventContainer.jsx
import React, { useState, useCallback, useMemo } from 'react';
import { HiCalendarDays, HiClock, HiMapPin, HiUser } from 'react-icons/hi2';
import Button from './ui/Button';
import Card from './ui/Card';
import Modal from './ui/Modal';
import HelpIcon from './Documentation/HelpIcon';
import ScheduleEntry from '../models/ScheduleEntry.ts';
import { getWeekNumber, parseTime } from '../utils/helpers.ts';

function EventContainer({
    baseScheduleItems,
    currentDate,
    isAdmin,
    isPublicView,
    onOpenScheduleModal,
    groups,
    subjects,
    teachers,
    rooms,
    activeDays
}) {
    // Event modal state
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    // Helper function to format event date
    const formatEventDate = useCallback((weekSeries, day, year) => {
        if (!weekSeries || !day || !year) return 'TBD';

        try {
            const weekNumber = parseInt(weekSeries.split(',')[0], 10);
            if (isNaN(weekNumber)) return 'TBD';

            // Calculate date from week number and day
            const jan4 = new Date(year, 0, 4);
            const dayOfWeek = jan4.getDay() || 7;
            const week1Monday = new Date(jan4);
            week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

            const targetMonday = new Date(week1Monday);
            targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

            const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(day.toLowerCase());
            if (dayIndex === -1) return 'TBD';

            const targetDate = new Date(targetMonday);
            if (dayIndex === 0) {
                targetDate.setDate(targetMonday.getDate() + 6);
            } else {
                targetDate.setDate(targetMonday.getDate() + (dayIndex - 1));
            }

            return targetDate.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting event date:', error);
            return 'TBD';
        }
    }, []);

    // Calculate upcoming events from schedule entries
    const upcomingEvents = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Start of today

        return baseScheduleItems
            .filter(entry => {
                // Only show events (not classes, workshops, etc.)
                if (entry.type !== 'event') return false;

                // Check if the event has valid date information
                if (!entry.weekSeries || !entry.day || !entry.year) return false;

                try {
                    // Calculate the actual date of the event
                    const weekNumber = parseInt(entry.weekSeries.split(',')[0], 10);
                    if (isNaN(weekNumber)) return false;

                    // Calculate date from week number and day
                    const jan4 = new Date(entry.year, 0, 4);
                    const dayOfWeek = jan4.getDay() || 7;
                    const week1Monday = new Date(jan4);
                    week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

                    const targetMonday = new Date(week1Monday);
                    targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

                    const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(entry.day.toLowerCase());
                    if (dayIndex === -1) return false;

                    const eventDate = new Date(targetMonday);
                    if (dayIndex === 0) {
                        eventDate.setDate(targetMonday.getDate() + 6);
                    } else {
                        eventDate.setDate(targetMonday.getDate() + (dayIndex - 1));
                    }

                    // Only show events from today onwards
                    return eventDate >= today;
                } catch (error) {
                    console.error('Error calculating event date:', error);
                    return false;
                }
            })
            .sort((a, b) => {
                try {
                    // Calculate actual dates for both events
                    const getEventDate = (entry) => {
                        const weekNumber = parseInt(entry.weekSeries.split(',')[0], 10);
                        const jan4 = new Date(entry.year, 0, 4);
                        const dayOfWeek = jan4.getDay() || 7;
                        const week1Monday = new Date(jan4);
                        week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);

                        const targetMonday = new Date(week1Monday);
                        targetMonday.setDate(week1Monday.getDate() + (weekNumber - 1) * 7);

                        const dayIndex = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].indexOf(entry.day.toLowerCase());
                        const eventDate = new Date(targetMonday);
                        if (dayIndex === 0) {
                            eventDate.setDate(targetMonday.getDate() + 6);
                        } else {
                            eventDate.setDate(targetMonday.getDate() + (dayIndex - 1));
                        }
                        return eventDate;
                    };

                    const dateA = getEventDate(a);
                    const dateB = getEventDate(b);

                    // Sort by date first
                    const dateComparison = dateA.getTime() - dateB.getTime();
                    if (dateComparison !== 0) return dateComparison;

                    // If same date, sort by start time
                    const timeA = a.timeStart ? parseTime(a.timeStart).startMinutes : 0;
                    const timeB = b.timeStart ? parseTime(b.timeStart).startMinutes : 0;
                    return timeA - timeB;
                } catch (error) {
                    console.error('Error sorting events:', error);
                    return 0;
                }
            });
    }, [baseScheduleItems]);

    // Helper function to detect if text contains HTML
    const isHtmlContent = useCallback((text) => {
        if (!text || typeof text !== 'string') return false;

        // Check for common HTML tags
        const htmlTagRegex = /<[^>]+>/;
        return htmlTagRegex.test(text);
    }, []);

    // Helper function to sanitize HTML content (basic sanitization)
    const sanitizeHtml = useCallback((html) => {
        if (!html) return '';

        // Remove potentially dangerous tags and attributes
        let sanitized = html
            .replace(/<script[^>]*>.*?<\/script>/gi, '') // Remove script tags
            .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '') // Remove iframe tags
            .replace(/<object[^>]*>.*?<\/object>/gi, '') // Remove object tags
            .replace(/<embed[^>]*>.*?<\/embed>/gi, '') // Remove embed tags
            .replace(/<link[^>]*>/gi, '') // Remove link tags
            .replace(/<meta[^>]*>/gi, '') // Remove meta tags
            .replace(/on\w+="[^"]*"/gi, '') // Remove event handlers
            .replace(/javascript:/gi, '') // Remove javascript: protocols
            .replace(/data:/gi, '') // Remove data: protocols
            .replace(/vbscript:/gi, ''); // Remove vbscript: protocols

        // Ensure links have proper styling and security attributes
        sanitized = sanitized.replace(/<a\s+([^>]*?)>/gi, (match, attributes) => {
            // Remove any existing style attributes and add our own
            const cleanAttributes = attributes
                .replace(/style="[^"]*"/gi, '')
                .replace(/target="[^"]*"/gi, '')
                .replace(/rel="[^"]*"/gi, '');

            return `<a ${cleanAttributes} style="color: #2563eb; text-decoration: underline;" target="_blank" rel="noopener noreferrer">`;
        });

        return sanitized;
    }, []);

    // Render text with HTML support, clickable links, and preserved newlines
    const renderTextWithLinks = useCallback((text) => {
        if (!text) return null;

        // Check if content is HTML
        if (isHtmlContent(text)) {
            const sanitizedHtml = sanitizeHtml(text);
            return (
                <div
                    className="text-sm text-gray-800 leading-relaxed break-words [&_a]:text-blue-600 [&_a]:underline [&_a]:hover:text-blue-800 [&_a]:break-words"
                    dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
                />
            );
        }

        // Original plain text handling with links
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const nodes = [];
        let lastIndex = 0;
        let match;
        let key = 0;
        while ((match = urlRegex.exec(text)) !== null) {
            const start = match.index;
            const url = match[0];
            if (start > lastIndex) {
                nodes.push(text.slice(lastIndex, start));
            }
            nodes.push(
                <a
                    key={`url-${key++}`}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-words"
                >
                    {url}
                </a>
            );
            lastIndex = start + url.length;
        }
        if (lastIndex < text.length) {
            nodes.push(text.slice(lastIndex));
        }
        const withLineBreaks = [];
        nodes.forEach((chunk, idx) => {
            if (typeof chunk === 'string') {
                const lines = chunk.split('\n');
                lines.forEach((line, li) => {
                    if (line) withLineBreaks.push(<React.Fragment key={`t-${idx}-${li}`}>{line}</React.Fragment>);
                    if (li < lines.length - 1) withLineBreaks.push(<br key={`br-${idx}-${li}`} />);
                });
            } else {
                withLineBreaks.push(chunk);
            }
        });
        return withLineBreaks;
    }, [isHtmlContent, sanitizeHtml]);

    // Modal handlers
    const openEventModal = useCallback((event) => {
        setSelectedEvent(event);
        setIsEventModalOpen(true);
    }, []);

    const closeEventModal = useCallback(() => {
        setIsEventModalOpen(false);
        setSelectedEvent(null);
    }, []);

    // Edit handler from Event modal (admin only)
    const handleEditFromEvent = useCallback(() => {
        if (!selectedEvent) return;
        setIsEventModalOpen(false);
        onOpenScheduleModal(selectedEvent);
    }, [selectedEvent, onOpenScheduleModal]);

    // Add new event handler
    const handleAddNewEventClick = useCallback(() => {
        // Create a template event item for adding
        const templateEvent = new ScheduleEntry({
            id: `temp-add-${Date.now()}`,
            day: activeDays[0] || 'monday', // Default to first active day
            timeStart: '10:00',
            timeEnd: '11:00',
            eventName: '',
            title: '',
            host: '', // Use host instead of teacher for events
            groups: [], // Empty groups means "All Groups" for events
            room: '',
            type: 'event',
            weekSeries: getWeekNumber(currentDate).toString(), // Default to current week for events
            year: currentDate.getFullYear(),
            description: ''
        });
        onOpenScheduleModal(templateEvent);
    }, [currentDate, onOpenScheduleModal]);

    // Render event entry
    const renderEventEntry = useCallback((event, index) => {
        // Format the event for display
        const eventDate = event.weekSeries && event.day && event.year ?
            formatEventDate(event.weekSeries, event.day, event.year) : 'TBD';
        const eventTime = event.timeRangeDisplay ? event.timeRangeDisplay() :
            (event.timeStart && event.timeEnd ? `${event.timeStart} - ${event.timeEnd}` : 'TBD');
        const eventLocation = event.location || event.room || 'TBD';
        const eventTitle = event.title || event.eventName || event.displayName || 'Untitled Event';
        const eventHost = event.teacher || null;

        const handleClick = () => {
            if (!event) return;
            if (isPublicView) {
                onOpenScheduleModal(event);
            } else {
                openEventModal(event);
            }
        };

        return (
            <li key={event?.id || index} className="border-b border-gray-200 py-3 px-1 hover:bg-blue-50 cursor-pointer" onClick={handleClick}>
                <p className="text-sm font-semibold text-blue-700">{eventTitle}</p>
                <p className="text-xs text-gray-600">{eventDate} @ {eventTime} - {eventLocation}</p>
                {eventHost && (
                    <p className="text-xs text-gray-500">Host: {eventHost}</p>
                )}
            </li>
        );
    }, [formatEventDate, openEventModal, onOpenScheduleModal, isPublicView]);

    return (
        <>
            {/* Events Panel */}
            <div className="lg:w-1/4 lg:min-w-[250px] lg:max-w-[300px] flex-shrink-0">
                <div className="relative mb-4">
                    <h2 className="text-xl font-semibold text-gray-700">Upcoming Events</h2>
                    <HelpIcon
                        documentationId="events"
                        size={16}
                        variant="info"
                        position="top-right"
                        className="ml-2"
                    />
                </div>
                <Card>
                    <div className="p-4">
                        {upcomingEvents && upcomingEvents.length > 0 ? (
                            <ul className="divide-y divide-gray-200 max-h-[calc(100vh-250px)] overflow-y-auto">
                                {upcomingEvents.filter(event => !!event).map(renderEventEntry)}
                            </ul>
                        ) : (
                            <p className="text-center text-gray-500 py-4">No upcoming events.</p>
                        )}
                        {isAdmin && (
                            <Button variant="secondary" className="w-full mt-4 text-sm" onClick={handleAddNewEventClick}>
                                Add New Event
                            </Button>
                        )}
                    </div>
                </Card>
            </div>

            {/* Event Details Modal */}
            <Modal
                isOpen={isEventModalOpen}
                onClose={closeEventModal}
                title={selectedEvent?.title || selectedEvent?.eventName || selectedEvent?.displayName || 'Event Details'}
            >
                {selectedEvent && (
                    <div className="space-y-6">
                        <div className="border-b pb-3">
                            <h3 className="text-2xl font-semibold text-gray-900">
                                {selectedEvent.title || selectedEvent.eventName || selectedEvent.displayName || 'Untitled Event'}
                            </h3>
                            {selectedEvent.teacher && (
                                <div className="mt-1 flex items-center gap-2 text-sm text-gray-600">
                                    <HiUser className="h-4 w-4 text-gray-500" />
                                    <span>Host: {selectedEvent.teacher}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-6">
                            <div className="flex items-center gap-2 min-w-[180px]">
                                <HiCalendarDays className="h-5 w-5 text-blue-600" />
                                <div className="text-sm text-gray-900">
                                    {selectedEvent.weekSeries && selectedEvent.day && selectedEvent.year
                                        ? formatEventDate(selectedEvent.weekSeries, selectedEvent.day, selectedEvent.year)
                                        : 'TBD'}
                                </div>
                            </div>
                            <div className="hidden sm:block text-gray-300">|</div>
                            <div className="flex items-center gap-2 min-w-[160px]">
                                <HiClock className="h-5 w-5 text-blue-600" />
                                <div className="text-sm text-gray-900">
                                    {selectedEvent.timeRangeDisplay
                                        ? selectedEvent.timeRangeDisplay()
                                        : (selectedEvent.timeStart && selectedEvent.timeEnd
                                            ? `${selectedEvent.timeStart} - ${selectedEvent.timeEnd}`
                                            : 'TBD')}
                                </div>
                            </div>
                            <div className="hidden sm:block text-gray-300">|</div>
                            <div className="flex items-center gap-2">
                                <HiMapPin className="h-5 w-5 text-blue-600" />
                                <div className="text-sm text-gray-900">
                                    {selectedEvent.location || selectedEvent.room || 'TBD'}
                                </div>
                            </div>
                        </div>

                        {selectedEvent.groups && selectedEvent.groups.length > 0 && (
                            <div>
                                <div className="text-xs text-gray-500 mb-1">Target Groups</div>
                                <div className="flex flex-wrap gap-2">
                                    {(Array.isArray(selectedEvent.groups) ? selectedEvent.groups : [selectedEvent.groups]).map((g, i) => {
                                        const label = typeof g === 'object' ? (g.name || g.group_code || g.id) : g;
                                        return (
                                            <span key={`${label}-${i}`} className="px-2 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                                                {label}
                                            </span>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {selectedEvent.description && (
                            <div className="border-t pt-4">
                                <div className="text-sm font-medium text-gray-500 mb-1">Description</div>
                                {renderTextWithLinks(selectedEvent.description)}
                            </div>
                        )}

                        <div className="mt-2 flex justify-end gap-3">
                            {isAdmin && (
                                <Button variant="primary" onClick={handleEditFromEvent}>Edit</Button>
                            )}
                            <Button variant="secondary" onClick={closeEventModal}>Close</Button>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
}

export default EventContainer;
